<template>
  <transition name="drawer">
    <div
      v-show="!isMobile || appStore.toolbarVisible"
      class="nav-mask"
      @click="handleClose"
      :style="wrapperStyle"
    >
      <div
        class="nav-container"
        ref="navContainerRef"
        :class="{ 'is-dragging': isDragging }"
        :style="containerStyle"
        @click.stop
      >
        <div v-if="isMobile" class="close-drawer-wrapper" @click="handleClose">
          <i-local-close-drawer class="close-drawer-icon" />
        </div>

        <div class="logo-wrapper">
          <img src="@/assets/images/logo/top-nav-left.png" class="logo-img" />
          <img src="@/assets/images/logo/top-nav-right.png" class="logo-img" />
        </div>

        <div class="functional-wrapper">
          <div
            v-if="isMobile && userStore.userDetail.user_id"
            class="welcome-text"
          >
            <span>{{ $t('common.hi') }}</span>
            <span>{{ userStore.userDetail.username }}</span>
          </div>

          <LanguageDropdown
            ref="langDropdownRef"
            :is-mobile="isMobile"
            :show="showLangDropdown"
            @toggle="toggleLangDropdown"
            @select="selectLang"
          />

          <UserMenu
            ref="userMenuRef"
            :is-mobile="isMobile"
            :show="showUserDropdown"
            @toggle="toggleUserDropdown"
            @logout="handleLogout"
            @login="handleLogin"
            @my-page="goMyPage"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { logoutUser } from '@/api'
import LanguageDropdown from './toolbar/LanguageDropdown.vue'
import UserMenu from './toolbar/UserMenu.vue'

const router = useRouter()
const userStore = useUserStore()
const appStore = useAppStore()
const { locale } = useI18n()

const props = defineProps({
  isMobile: { type: Boolean, default: false }
})

// ==================== Refs ====================
const navContainerRef = ref(null)
const langDropdownRef = ref(null)
const userMenuRef = ref(null)

// ==================== 状态管理 ====================
const startX = ref(0)
const translateX = ref(0)
const isDragging = ref(false)
const showLangDropdown = ref(false)
const showUserDropdown = ref(false)

// ==================== 计算属性 ====================
const containerStyle = computed(() => {
  if (!props.isMobile) return {}
  return {
    transform: translateX.value !== 0 ? `translateX(${translateX.value}px)` : ''
  }
})

const wrapperStyle = computed(() => {
  if (!props.isMobile) return {}
  if (isDragging.value) {
    const containerWidth = navContainerRef.value?.offsetWidth || 1
    const progress = Math.max(
      0,
      1 - Math.abs(translateX.value) / containerWidth
    )
    return {
      backgroundColor: `rgba(0, 0, 0, ${0.3 * progress})`,
      transition: 'none'
    }
  }
  return {}
})

// ==================== 语言切换 ====================
const toggleLangDropdown = () => {
  showLangDropdown.value = !showLangDropdown.value
  if (showLangDropdown.value) {
    showUserDropdown.value = false
  }
}

const selectLang = (lang) => {
  locale.value = lang
  localStorage.setItem('lang', lang)
  showLangDropdown.value = false
}

// ==================== 用户菜单 ====================
const toggleUserDropdown = () => {
  showUserDropdown.value = !showUserDropdown.value
  if (showUserDropdown.value) {
    showLangDropdown.value = false
  }
}

const goMyPage = () => {
  appStore.toolbarVisible = false
  router.push('/my')
}

const handleLogout = async () => {
  const res = await logoutUser()
  if (res.code === 200) {
    localStorage.removeItem('token')
    localStorage.removeItem('user_id')
    userStore.setUserDetail({})
    showUserDropdown.value = false
  }
  Toast(res.info)
}

const handleLogin = () => {
  if (props.isMobile) appStore.toolbarVisible = false
  appStore.loginVisible = true
}

// ==================== 点击外部关闭 ====================
const handleClickOutside = (e) => {
  if (langDropdownRef.value && !langDropdownRef.value.$el.contains(e.target)) {
    showLangDropdown.value = false
  }
  if (userMenuRef.value && !userMenuRef.value.$el.contains(e.target)) {
    showUserDropdown.value = false
  }
}

// ==================== 抽屉交互 ====================
const handleClose = () => {
  if (!props.isMobile) return
  appStore.toolbarVisible = false
}

const handleTouchStart = (e) => {
  if (!props.isMobile) return
  startX.value = e.touches[0].clientX
  isDragging.value = true
}

const handleTouchMove = (e) => {
  if (!props.isMobile || !isDragging.value) return
  const moveX = e.touches[0].clientX - startX.value
  if (e.cancelable) {
    e.preventDefault()
  }
  if (moveX < 0) {
    translateX.value = moveX
  }
}

const handleTouchEnd = () => {
  if (!props.isMobile) return
  isDragging.value = false
  const containerWidth = navContainerRef.value?.offsetWidth || 0
  if (Math.abs(translateX.value) > containerWidth * 0.3) {
    handleClose()
  }
  translateX.value = 0
}

// ==================== 生命周期 ====================
// 锁定背景滚动
watch(
  () => [appStore.toolbarVisible, props.isMobile],
  ([visible, mobile]) => {
    if (visible && mobile) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  },
  { immediate: true }
)

// 显式绑定以确保 passive: false，解决 Android 兼容性
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  if (navContainerRef.value) {
    navContainerRef.value.addEventListener('touchstart', handleTouchStart, {
      passive: true
    })
    navContainerRef.value.addEventListener('touchmove', handleTouchMove, {
      passive: false
    })
    navContainerRef.value.addEventListener('touchend', handleTouchEnd, {
      passive: true
    })
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (navContainerRef.value) {
    navContainerRef.value.removeEventListener('touchstart', handleTouchStart)
    navContainerRef.value.removeEventListener('touchmove', handleTouchMove)
    navContainerRef.value.removeEventListener('touchend', handleTouchEnd)
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
  }
})
</script>

<style lang="scss" scoped>
.nav-mask {
  width: 100%;
  z-index: 1000;
  @include adapt($prop: height, $h5: 100%, $pc: 80);

  @include respond-down('md') {
    position: fixed;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.3);
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
  }

  @include respond-up('md') {
    flex-shrink: 0;
  }

  .nav-container {
    position: relative;
    display: flex;
    @include adapt($prop: flex-direction, $h5: column, $pc: row);
    flex-shrink: 0;
    height: 100%;
    @include adapt($prop: width, $h5: 570, $pc: 100%);
    @include adapt($prop: min-height, $h5: 100dvh, $pc: auto);
    @include adapt($prop: padding, $h5: 48 55, $pc: 0 55);
    // @include adapt($prop: background, $h5: #2a3357, $pc: rgba(0, 0, 0, 0.5));
    @include adapt($prop: background, $h5: #161f32, $pc: rgba(0, 0, 0, 0.5));
    color: #fff;
    transition: transform 0.3s cubic-bezier(0.32, 1, 0.23, 1);
    will-change: transform;

    &.is-dragging {
      transition: none;
    }

    @include respond-up('md') {
      justify-content: space-between;
      align-items: center;
    }

    .close-drawer-wrapper {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(30%, -50%);
      @include flex-center;
      width: h5(50);
      height: h5(200);
      border-radius: h5(10);
      border: 0 solid transparent;
      border-right: 1px solid #c1c8e4;
      // background: #2a3357;
      background: #161f33;
      cursor: pointer;

      .close-drawer-icon {
        width: h5(60);
        height: h5(100);
      }
    }

    .logo-wrapper {
      display: flex;
      align-items: center;
      @include adapt($prop: gap, $h5: 20, $pc: 17);
      @include respond-down('md') {
        flex-shrink: 0;
        padding-bottom: h5(38);
        border-bottom: h5(2) solid #1f2746;
      }

      .logo-img {
        @include adapt($prop: width, $h5: 220, $pc: 140);
        @include adapt($prop: height, $h5: 80, $pc: 50);
      }
    }

    .functional-wrapper {
      display: flex;
      @include adapt($prop: flex-direction, $h5: column, $pc: row);
      @include adapt($prop: gap, $h5: 83, $pc: 97);

      @include respond-down('md') {
        flex: 1;
        padding-top: h5(66);
      }

      .welcome-text {
        display: flex;
        flex-direction: column;
        font-size: h5(72);
        font-weight: bold;
      }
    }
  }
}

/* 动画部分 */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s linear;

  .nav-container {
    transition: transform 0.3s cubic-bezier(0.32, 1, 0.23, 1);
  }
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  .nav-container {
    transform: translateX(-100%);
  }
}

.dropdown-enter-active,
.dropdown-leave-active,
.dropdown-pc-enter-active,
.dropdown-pc-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-pc-enter-from,
.dropdown-pc-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
